/**
 * kunstkaesten-stills – Static PNG exports of each Kunstkaesten step
 *
 * Renders the end state of each cross step (the still frame that the animated
 * videos hold at the end). One panel is active; the other three are blurred
 * to the inactive state. No animation — exports a single PNG.
 *
 * Use the "Active Step" input to select which panel faces front.
 */

import React, { useEffect, useRef } from "react";
import { animate } from "animejs";
import { COMPONENT_ORDER } from "../kunstkaesten/useCrossRotation";
import "../kunstkaesten/styles.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const FLIPPED_PANELS = new Set(["ausblick", "info"]);
const BASE_ROTATIONS = { aktuell: 0, ausblick: 90, info: 0, archiv: 90 };
const POSITION_INDEX = { aktuell: 0, ausblick: 1, info: 2, archiv: 3 };
const ACTIVE_STEPS = ["1", "2", "3", "4"];
const STEP_KEY = { "1": "aktuell", "2": "ausblick", "3": "info", "4": "archiv" };

/** Absolute rotation for each panel after `steps` × 90° have been applied. */
function rotationsAtStep(steps) {
  return Object.fromEntries(
    COMPONENT_ORDER.map((k) => [k, BASE_ROTATIONS[k] + steps * 90]),
  );
}

// ─── Handler ──────────────────────────────────────────────────────────────────

export const handler = ({ inputs, mechanic }) => {
  const {
    width,
    height,
    backgroundColor,
    textColor,
    inactiveBlur,
    inactiveOpacity,
    activeStep,
    label1,
    label2,
    label3,
    label4,
    exhibitionTitle,
    artistName,
    dateSpan,
    vernissageDate,
    vernissageTime,
    vernissageAddress,
    exhibitionTheme,
    artistDetails,
    imageAktuell,
    imageAusblick,
    imageInfo,
    imageArchiv,
  } = inputs;

  const activePanelKey = STEP_KEY[activeStep];

  const panelImages = {
    aktuell: imageAktuell,
    ausblick: imageAusblick,
    info: imageInfo,
    archiv: imageArchiv,
  };

  // Create object URLs once (same guard as the animated function).
  const panelImageUrlsRef = useRef(undefined);
  if (panelImageUrlsRef.current === undefined) {
    panelImageUrlsRef.current = Object.fromEntries(
      Object.entries(panelImages).map(([key, img]) => [
        key,
        img ? URL.createObjectURL(img) : null,
      ]),
    );
  }
  const panelImageUrls = panelImageUrlsRef.current;

  const panelLabels = {
    aktuell: label1,
    ausblick: label2,
    info: label3,
    archiv: label4,
  };

  // ── Refs ───────────────────────────────────────────────────────────────
  const panelRefs = useRef({});
  const setPanelRef = (key) => (el) => { panelRefs.current[key] = el; };

  const panelImgRefs = useRef({});
  const setPanelImgRef = (key) => (el) => { panelImgRefs.current[key] = el; };

  const imgSlotRefs = useRef({});
  const setImgSlotRef = (key) => (el) => { imgSlotRefs.current[key] = el; };

  // ── Instantly apply end-state styles, then capture ─────────────────────
  useEffect(() => {
    const BLUR_INACTIVE = `blur(${inactiveBlur}px)`;
    const OPACITY_INACTIVE = inactiveOpacity;
    const rots = rotationsAtStep(POSITION_INDEX[activePanelKey]);

    // Kill CSS transitions on all panels and their text children so that
    // anime.js duration:0 styles take effect immediately (no 0.6s transition
    // fighting the instant-set values before mechanic.done() is called).
    COMPONENT_ORDER.forEach((key) => {
      const el = panelRefs.current[key];
      if (!el) return;
      el.style.transition = "none";
      const stepname = el.querySelector(".cross-stepname");
      const body = el.querySelector(".cross-body");
      if (stepname) stepname.style.transition = "none";
      if (body) body.style.transition = "none";
    });

    COMPONENT_ORDER.forEach((key) => {
      const el = panelRefs.current[key];
      if (!el) return;
      const isFlipped = FLIPPED_PANELS.has(key);
      const isActive = key === activePanelKey;

      if (panelImageUrls[key]) {
        // Image panel: container is always a transparent wrapper (opacity:1,
        // no filter). Text elements are individually blurred so they can never
        // appear unblurred regardless of which step is active.
        animate(el, {
          opacity: 1,
          filter: "blur(0px)",
          rotate: rots[key],
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: 0,
        });
        const stepname = el.querySelector(".cross-stepname");
        const body = el.querySelector(".cross-body");
        if (stepname) animate(stepname, { opacity: OPACITY_INACTIVE, filter: BLUR_INACTIVE, duration: 0 });
        if (body) animate(body, { opacity: OPACITY_INACTIVE, filter: BLUR_INACTIVE, duration: 0 });
      } else {
        animate(el, {
          opacity: isActive ? 1 : OPACITY_INACTIVE,
          filter: isActive ? "blur(0px)" : BLUR_INACTIVE,
          rotate: rots[key],
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: 0,
        });
      }

      // Mirror rotation to image slot so the image rotates with its panel.
      if (imgSlotRefs.current[key]) {
        animate(imgSlotRefs.current[key], {
          rotate: rots[key],
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: 0,
        });
      }

      // Show image only for the active step; hide for all others.
      if (panelImageUrls[key] && panelImgRefs.current[key]) {
        animate(panelImgRefs.current[key], { opacity: isActive ? 1 : 0, duration: 0 });
      }
    });

    // Wait one frame so anime.js inline styles are flushed to the DOM.
    requestAnimationFrame(() => mechanic.done());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Panel body content ─────────────────────────────────────────────────
  const panelContent = {
    aktuell: (
      <>
        <h1 className="cross-line">{artistName}</h1>
        <h1 className="cross-line" style={{ fontStyle: "italic" }}>
          {exhibitionTitle}
        </h1>
        <h1 className="cross-line">{dateSpan}</h1>
      </>
    ),
    ausblick: (
      <>
        <h1 className="cross-line">{vernissageDate}</h1>
        <h1 className="cross-line">{vernissageAddress}</h1>
        <h1 className="cross-line">{vernissageTime}</h1>
      </>
    ),
    info: (
      <>
        <p className="cross-line-wrap" style={{ width: "170%" }}>
          {exhibitionTheme}
        </p>
      </>
    ),
    archiv: (
      <>
        <p className="cross-line-wrap" style={{ width: "170%" }}>
          {artistDetails}
        </p>
      </>
    ),
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div
      className="cross-root"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor,
        color: textColor,
      }}
    >
      {/* Image slots — rendered before panels so they sit below all text */}
      {COMPONENT_ORDER.map((key) => {
        const imgUrl = panelImageUrls[key];
        if (!imgUrl) return null;
        return (
          <div
            key={`img-${key}`}
            ref={setImgSlotRef(key)}
            className={`cross-image-slot cross-image-slot-${key}`}
          >
            <img
              ref={setPanelImgRef(key)}
              src={imgUrl}
              alt=""
              className="cross-panel-image"
              style={{ opacity: 0 }}
            />
          </div>
        );
      })}
      {/* Panel containers */}
      {COMPONENT_ORDER.map((key) => (
        <div key={key} className="cross-container">
          <div ref={setPanelRef(key)} className={`cross-panel cross-${key}`}>
            <h3 className="cross-stepname">{panelLabels[key]}</h3>
            <div className="cross-body">{panelContent[key]}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Inputs ───────────────────────────────────────────────────────────────────

export const inputs = {
  // ── Canvas ──────────────────────────────────────────────────────────────
  width: {
    type: "number",
    default: 1080,
    label: "Width (px)",
  },
  height: {
    type: "number",
    default: 1350,
    label: "Height (px)",
  },
  backgroundColor: {
    type: "color",
    model: "hex",
    default: "#ffffff",
    label: "Background",
  },
  textColor: {
    type: "color",
    model: "hex",
    default: "#0a1ef5",
    label: "Text Color",
  },
  inactiveBlur: {
    type: "number",
    default: 9,
    min: 0,
    max: 40,
    slider: true,
    label: "Inactive Blur (px)",
  },
  inactiveOpacity: {
    type: "number",
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.05,
    slider: true,
    label: "Inactive Opacity",
  },

  // ── Step selector ──────────────────────────────────────────────────────
  activeStep: {
    type: "text",
    default: "1",
    options: ACTIVE_STEPS,
    label: "Active Step",
  },

  // ── Step name labels ──────────────────────────────────────────────────
  label1: {
    type: "text",
    default: "Neue Ausstellung",
    label: "Label – 1",
  },
  label2: {
    type: "text",
    default: "Vernissage",
    label: "Label – 2",
  },
  label3: {
    type: "text",
    default: "Zum Thema",
    label: "Label – 3",
  },
  label4: {
    type: "text",
    default: "Über die Künstler:in",
    label: "Label – 4",
  },

  // ── Step 1 – Aktuell ──────────────────────────────────────────────────
  exhibitionTitle: {
    type: "text",
    default: "«Orte der Stille»",
    label: "Exhibition Title",
  },
  artistName: {
    type: "text",
    default: "Alexandre Joly",
    label: "Artist Name",
  },
  dateSpan: {
    type: "text",
    default: "01.03. – 02.06.26",
    label: "Date Span",
  },

  // ── Step 2 – Ausblick (Vernissage) ────────────────────────────────────
  vernissageDate: {
    type: "text",
    default: "Freitag, 28. Februar 2026",
    label: "Vernissage Date",
  },
  vernissageAddress: {
    type: "text",
    default: "Musterstrasse 12, 8000 Zürich",
    label: "Vernissage Address",
  },
  vernissageTime: {
    type: "text",
    default: "18:00 Uhr",
    label: "Vernissage Time",
  },

  // ── Step 3 – Info (Theme) ─────────────────────────────────────────────
  exhibitionTheme: {
    type: "text",
    default:
      "Die Ausstellung erkundet das Spannungsfeld zwischen urbanem Raum und Natur.",
    label: "Exhibition Theme / Description",
  },

  // ── Step 4 – Archiv (Artist) ──────────────────────────────────────────
  artistDetails: {
    type: "text",
    default:
      "Vorname Nachname (*1990) lebt und arbeitet in Zürich. Studium an der ZHdK.",
    label: "Artist Details / Bio",
  },

  // ── Step images (optional) ────────────────────────────────────────────
  imageAktuell: {
    type: "image",
    label: "Image – 1",
  },
  imageAusblick: {
    type: "image",
    label: "Image – 2",
  },
  imageInfo: {
    type: "image",
    label: "Image – 3",
  },
  imageArchiv: {
    type: "image",
    label: "Image – 4",
  },
};

// ─── Presets ──────────────────────────────────────────────────────────────────

export const presets = {
  portrait: { width: 1080, height: 1350 },
  square:   { width: 1080, height: 1080 },
};

// ─── Settings ─────────────────────────────────────────────────────────────────

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Kunstkaesten Stills",
  animated: false,
  hideScaleToFit: true,
  initialScaleToFit: false,
  hideAutoRefresh: true,
  hideFunction: true,
};
